import type { MechanicDefinition } from "./types";
import { INJECTION_TARGETS, type InjectionTarget } from "./custom-rules";

const PROMPT_FIELDS = INJECTION_TARGETS.map((t) => t.value);

/** Build per-target config schema fields */
function buildPromptFields() {
  return INJECTION_TARGETS.map((t) => ({
    key: `prompt_${t.value}` as string,
    label: `${t.label} Prompt Addition`,
    type: "textarea" as const,
    default: "",
    description: `Custom system-prompt text injected into the ${t.label} agent context.`,
    placeholder: `Additional instructions for ${t.label}…`,
    rows: 3,
  }));
}

/** Get the prompt text for a specific injection target */
export function getCustomAIPrompt(config: Record<string, any>, target: InjectionTarget): string {
  return (config[`prompt_${target}`] as string) || "";
}

/** Format all non-empty custom AI prompts into a system prompt block */
export function formatCustomAIBlock(config: Record<string, any>): string {
  const lines: string[] = [];
  for (const t of INJECTION_TARGETS) {
    const text = getCustomAIPrompt(config, t.value);
    if (text.trim()) {
      lines.push(`### CUSTOM_AI — ${t.label}\n${text.trim()}`);
    }
  }
  if (lines.length === 0) return "";
  return `## CUSTOM AI INSTRUCTIONS\n${lines.join("\n\n")}`;
}

export const customAI: MechanicDefinition = {
  id: "custom_ai",
  disabled: true,
  name: "Custom AI Prompts",
  description: "Inject custom system-prompt text into specific AI agents (narrator, NPC, combat, etc.). Use for fine-tuning AI behavior beyond what mechanics provide.",
  icon: "BrainCircuit",
  category: "core",
  aiHints: {
    purpose: "Custom prompt additions per agent type for fine-tuning AI behavior.",
    whenToUse: "When builders need to inject specific instructions that don't fit any existing mechanic.",
    synergies: ["custom_rules"],
    conflicts: [],
    prerequisites: [],
    tags: ["custom-ai", "provider", "byok", "system-level", "prompts"],
    complexity: 2,
    auditPasses: 1,
    exampleConfig: {
      locked: false,
      prompt_narrator: "",
      prompt_npc_agent: "",
      prompt_director: "",
    },
    stateVariables: [],
  },
  configSchema: [
    {
      key: "locked",
      label: "Lock Prompts",
      type: "boolean",
      default: false,
      description: "When locked, the builder AI cannot modify custom AI prompts.",
    },
    ...buildPromptFields(),
  ],
  systemPromptSection: (config) => {
    return formatCustomAIBlock(config);
  },
  tools: [
    {
      type: "function",
      function: {
        name: "update_custom_ai_prompts",
        description: "Update the custom AI prompt addition for a specific agent target. Only available when the custom_ai mechanic is active and not locked.",
        parameters: {
          type: "object",
          properties: {
            target: {
              type: "string",
              enum: PROMPT_FIELDS,
              description: "Which agent target to update the prompt for.",
            },
            prompt: {
              type: "string",
              description: "The new prompt text to inject into this agent's context.",
            },
          },
          required: ["target", "prompt"],
        },
      },
    },
  ],
};
